import { TranslationKey } from '@Localization';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useEffect, useState } from 'react';
import { Redirect } from 'react-router';
import { useOnMount } from '../../../../common/hooks/useOnMount';
import { ColumnLayout } from '../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { RowLayout } from '../../../../modules/common/layouts/rowLayout/RowLayout';
import { SpaceBetweenLayout } from '../../../../modules/common/layouts/spaceBetweenLayout/SpaceBetweenLayout';
import { Settings } from '../../../../modules/settings/SettingsContext';
import { WorkspaceActionsInfo } from '../../../../modules/team/workspaceActionsInfo/WorkspaceActionsInfo';
import { ICurrentWorkspaceStore } from '../../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { getEmailStatus } from '../../../helpers/email.helpers';
import { useLoader } from '../../../helpers/useLoader';
import { ACDLError, InputStatus } from '../../../models/error.model';
import { SortingDirection } from '../../../models/sortingDirection.model';
import { Home } from '../../../routes/config/Home';
import { IUserStore } from '../../user/user.store';
import { WorkspaceRole } from '../../workspaces/workspaces.store';
import { AddTeamMember } from '../components/AddTeamMember';
import { ChangeUserRoleModal } from '../components/ChangeUserRoleModal';
import { RemoveUserModal } from '../components/RemoveUserModal';
import { TeamList } from '../components/TeamList';
import { ITeamService } from '../team.bl';
import { useTeamPermissions } from '../team.permissions';
import { ITeamStore } from '../team.store';
import { StatisticsTimeFrameSwitchContainer } from './StatisticsTimeFrameSwitch.container';

export const TeamContainer = observer((): ReactElement => {
  const userStore = useInjection(IUserStore.$);
  const teamService = useInjection(ITeamService.$);
  const teamStore = useInjection(ITeamStore.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const teamPermissions = useTeamPermissions();

  const settingsContext = useContext(Settings);
  const { isLoading, withLoaderAsync } = useLoader();

  const [email, setEmail] = useState<string>('');
  const [emailStatus, setEmailStatus] = useState<InputStatus>(InputStatus.empty());
  const [selectedRole, setSelectedRole] = useState<WorkspaceRole>(WorkspaceRole.Collaborator);
  const [selectedChangeRole, setSelectedChangeRole] = useState<WorkspaceRole>(WorkspaceRole.Collaborator);
  const [removeUserModalShown, setRemoveUserModalShown] = useState<boolean>(false);
  const [userSelectedToRemove, setUserSelectedToRemove] = useState<string>('');
  const [changeUserRoleModalShown, setChangeUserRoleModalShown] = useState<boolean>(false);
  const [userSelectedToChange, setUserSelectedToChange] = useState<string>('');
  const [userNameSelectedToChange, setUserNameSelectedToChange] = useState<string>('');
  const [changeRoleError, setChangeRoleError] = useState<TranslationKey>('');
  const [errorContext, setErrorContext] = useState<object>({});

  useEffect(() => {
    return (): void => {
      teamService.dispose();
    };
  }, []);

  const handleEmailChange = (email: string): void => {
    setEmail(email);
    const emailStatus = getEmailStatus(email, [], true);
    setEmailStatus(emailStatus);
  };

  const handleRoleSelected = (role: WorkspaceRole): void => {
    setSelectedRole(role);
  };

  const handleChangeRoleSelected = (selectedChangeRole: WorkspaceRole): void => {
    setSelectedChangeRole(selectedChangeRole);
    setChangeRoleError('');
  };

  const handleAddNewUser = async (): Promise<void> => {
    if (!emailStatus.isValid || isLoading) {
      return;
    }

    await withLoaderAsync(async (): Promise<void> => {
      const result = await teamService.addUserToWorkspaceAsync(email, selectedRole);

      if (result instanceof ACDLError && result.apiErrorResponse) {
        setErrorContext(result.apiErrorResponse.errorContext ?? {});
        setEmailStatus(InputStatus.buildFrom([getUniqueLimitError(result.apiErrorResponse.errorCodes[0])]));
      } else {
        setEmail('');
        setEmailStatus(InputStatus.empty());
      }
    }, 'add-user-button');
  };

  const getUniqueLimitError = (errorCode: TranslationKey): TranslationKey => {
    const canSeeOwnerErrors = teamPermissions.canSeeOwnerErrors();

    return canSeeOwnerErrors && (errorCode === 'UNIQUE_WORKERS_LIMIT_REACHED' || errorCode === 'UNIQUE_USERS_LIMIT_REACHED') ? `${errorCode}_OWNER` : errorCode;
  };

  const toggleRemoveUserModal = (email: string): void => {
    setRemoveUserModalShown((prevState: boolean) => !prevState);
    setUserSelectedToRemove(email);
  };

  const handleRemoveUser = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    await withLoaderAsync(async (): Promise<void> => {
      await teamService.removeUserFromWorkspaceAsync(userSelectedToRemove);
      setUserSelectedToRemove('');
      setRemoveUserModalShown(false);
      clearErrors();
    }, 'remove-user-button');
  };

  const toggleChangeUserRoleModal = (email: string): void => {
    const user = teamStore.teamMembers.find((m) => m.email === email);
    setChangeUserRoleModalShown((prevState: boolean) => !prevState);
    setUserSelectedToChange(email);
    setUserNameSelectedToChange(user && user.name ? user.name : '');
    setSelectedChangeRole(user ? user.role : WorkspaceRole.Collaborator);
    setChangeRoleError('');
  };

  const handleChangeUserRole = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    await withLoaderAsync(async (): Promise<void> => {
      const result = await teamService.changeUserRoleAsync(userSelectedToChange, selectedChangeRole);

      if (result instanceof ACDLError && result.apiErrorResponse) {
        setChangeRoleError(getUniqueLimitError(result.apiErrorResponse.errorCodes[0]));
      } else {
        setUserSelectedToChange('');
        setChangeUserRoleModalShown(false);
        clearErrors();
      }
    }, 'change-user-role-button');
  };

  const handleChangeSorting = async (orderBy: string, orderDirection: SortingDirection): Promise<void> => {
    await teamService.changeSorting(orderBy, orderDirection);
  };

  const clearErrors = (): void => {
    const canManageTeamMembers = teamPermissions.canManageTeamMembers().allowed;

    if (!canManageTeamMembers) {
      setEmail('');
      setEmailStatus(InputStatus.empty());
    } else if (email !== '') {
      setEmailStatus(getEmailStatus(email, [], true));
    }
  };

  const handlePaginationChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    await teamService.changePagination(pageNumber, pageSize);
    settingsContext.setTeamPageSize(pageSize);
  };

  const canViewTeamMembers = teamPermissions.canViewTeamMembers();

  if (!currentWorkspaceStore.currentWorkspace.id) {
    return <></>;
  }

  useOnMount(async () => {
    await teamService.initializeAsync();
  });

  return !canViewTeamMembers ? (
    <Redirect to={Home.Projects.List.All.withParams({ workspaceId: currentWorkspaceStore.currentWorkspace.id })} />
  ) : (
    <ColumnLayout size='md'>
      <SpaceBetweenLayout wrap>
        <AddTeamMember
          email={email}
          emailStatus={emailStatus}
          selectedRole={selectedRole}
          currentWorkspaceId={currentWorkspaceStore.currentWorkspace.id}
          onRoleSelected={handleRoleSelected}
          onAddNewUser={handleAddNewUser}
          onEmailChange={handleEmailChange}
          clearErrors={clearErrors}
          errorContext={errorContext}
        />
        <RowLayout
          size={'sm'}
          className='team-options'
        >
          <WorkspaceActionsInfo />
          <StatisticsTimeFrameSwitchContainer />
        </RowLayout>
      </SpaceBetweenLayout>

      <TeamList
        isLoading={teamStore.isLoading}
        currentUserEmail={userStore.userInfo.email}
        teamMembers={teamStore.teamMembers.slice()}
        onToggleRemoveUserModal={toggleRemoveUserModal}
        onToggleChangeUserRoleModal={toggleChangeUserRoleModal}
        onSortChange={handleChangeSorting}
        orderBy={teamStore.orderBy}
        orderDirection={teamStore.orderDirection}
        pagination={{
          ...teamStore.pagination,
          pageSize: settingsContext.store.teamPageSize,
          onChange: handlePaginationChange,
        }}
      />

      <RemoveUserModal
        email={userSelectedToRemove}
        isShown={removeUserModalShown}
        onConfirm={handleRemoveUser}
        onCancel={() => {
          toggleRemoveUserModal('');
        }}
      />
      <ChangeUserRoleModal
        changeUserRoleModalShown={changeUserRoleModalShown}
        selectedChangeRole={selectedChangeRole}
        onChangeRoleSelected={handleChangeRoleSelected}
        onChange={handleChangeUserRole}
        onToggleChangeUserRoleModal={toggleChangeUserRoleModal}
        userSelectedToChange={userSelectedToChange}
        userNameSelectedToChange={userNameSelectedToChange}
        changeRoleError={changeRoleError}
        currentPlan={userStore.planInfo.plan}
      />
    </ColumnLayout>
  );
});
